import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { CaretRight, ArrowLeft, House } from "@phosphor-icons/react";
import { getTool, TOOLS, CATEGORIES } from "@/data/tools";

import WordCounter from "@/pages/tools/WordCounter";
import CaseConverter from "@/pages/tools/CaseConverter";
import LoremIpsum from "@/pages/tools/LoremIpsum";
import PasswordGenerator from "@/pages/tools/PasswordGenerator";
import QrCodeGenerator from "@/pages/tools/QrCodeGenerator";
import BulkUrlOpener from "@/pages/tools/BulkUrlOpener";
import MetaTagGenerator from "@/pages/tools/MetaTagGenerator";
import SerpPreview from "@/pages/tools/SerpPreview";
import RobotsTxtGenerator from "@/pages/tools/RobotsTxtGenerator";
import KeywordDensity from "@/pages/tools/KeywordDensity";
import JsonFormatter from "@/pages/tools/JsonFormatter";
import Base64Tool from "@/pages/tools/Base64Tool";
import UuidGenerator from "@/pages/tools/UuidGenerator";
import HashGenerator from "@/pages/tools/HashGenerator";
import FavoriteButton from "@/components/FavoriteButton";

const REGISTRY = {
  "word-counter": WordCounter,
  "case-converter": CaseConverter,
  "lorem-ipsum": LoremIpsum,
  "password-generator": PasswordGenerator,
  "qr-code-generator": QrCodeGenerator,
  "bulk-url-opener": BulkUrlOpener,
  "meta-tag-generator": MetaTagGenerator,
  "serp-preview": SerpPreview,
  "robots-txt-generator": RobotsTxtGenerator,
  "keyword-density": KeywordDensity,
  "json-formatter": JsonFormatter,
  "base64": Base64Tool,
  "uuid-generator": UuidGenerator,
  "hash-generator": HashGenerator,
};

export default function ToolPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tool = getTool(slug);
  const Component = REGISTRY[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!tool || !Component) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-32 text-center">
        <p className="font-mono text-sm text-accent">404</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter">
          Tool not found
        </h1>
        <Link
          to="/"
          data-testid="back-home"
          className="mt-6 inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-primary-foreground"
        >
          <House size={16} weight="bold" /> Back to tools
        </Link>
      </div>
    );
  }

  const Icon = tool.icon;
  const catLabel = CATEGORIES.find((c) => c.id === tool.category)?.label;
  const related = TOOLS.filter((t) => t.category === tool.category && t.slug !== tool.slug).slice(0, 3);

  return (
    <div>
      {/* Tool header */}
      <section className="border-b border-border/70 bg-card">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
          <nav className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary" data-testid="breadcrumb-home">Tools</Link>
            <CaretRight size={12} />
            <span>{catLabel}</span>
            <CaretRight size={12} />
            <span className="text-foreground">{tool.name}</span>
          </nav>

          <div className="mt-6 flex items-start gap-5">
            <span className="grid h-14 w-14 shrink-0 place-items-center border border-border bg-primary text-primary-foreground">
              <Icon size={28} weight="duotone" />
            </span>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
                  {tool.name}
                </h1>
                <span className="hidden font-mono text-xs text-muted-foreground sm:inline">
                  {tool.code}
                </span>
                <FavoriteButton slug={tool.slug} className="ml-1 h-9 w-9" size={18} />
              </div>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {tool.short}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tool body */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <Component />
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-12">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              Related {catLabel} tools
            </h2>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-primary hover:underline"
              data-testid="all-tools-link"
            >
              <ArrowLeft size={14} weight="bold" /> All tools
            </button>
          </div>
          <div className="mt-5 grid grid-cols-1 border-l border-t border-border/70 sm:grid-cols-3">
            {related.map((t) => {
              const RIcon = t.icon;
              return (
                <Link
                  key={t.slug}
                  to={`/tool/${t.slug}`}
                  data-testid={`related-${t.slug}`}
                  className="group flex items-center gap-4 border-b border-r border-border/70 bg-card p-5 transition-colors hover:border-primary"
                >
                  <span className="grid h-10 w-10 place-items-center border border-border text-primary group-hover:bg-primary group-hover:text-primary-foreground">
                    <RIcon size={20} weight="duotone" />
                  </span>
                  <span className="font-display text-sm font-bold tracking-tight">{t.name}</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
